import { useState, useEffect } from 'react';
import { useHistory } from 'react-router'
import moment from 'moment';

import { Button, Form, message, Input, Modal, Radio, Select, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import TextArea from 'antd/lib/input/TextArea';

import notifier from 'utils/notifier';
import { AuthModal, ErrorResponse } from 'utils/types';
import { getDisplayCompanyId } from 'utils/helpers';
import { StatusCode } from 'utils/enums';

import { GetSubmittedFileNames } from 'store/authorization/files/actions';
import { ModifyModal } from 'store/authorization/extendAndModify/actions';

import FloatLabel from 'components/Common/FloatLabel';

import './index.scss';

const { Title } = Typography;
const { Dragger } = Upload;

interface ModifyAuthModalProps {
  authDetailState: AuthModal,
  isModalVisible: boolean,
  user: string,
  getSubmittedFileNames(model: GetSubmittedFileNames): any,
  uploadFilestoAuth(options: any): any,
  modifyAuth(model: ModifyModal): any,
  isModifyingAuth: boolean,
  isUploading: boolean,
  isModalVisibleHandler(): any,
  getAuthMember(search: any): any,
  getProvider(providerKeyId: string, companyId: string): any,
  getPortalAuth(authNo: string, company: string, userRole: number): any,
}

const ModifyAuthDetailModal = (props: ModifyAuthModalProps) => {
  const history = useHistory();

  const [form] = Form.useForm();
  const [fileAttachments, setFileAttachments] = useState<any[]>([]);
  const [provider, setProvider] = useState<any>([]);
  const [modifyAuthReferralBy, setModifyAuthReferralBy] = useState<any>([]);
  const [isDisabledButton, setIsDisabledButton] = useState('');

  useEffect(() => {
    if (props.isModalVisible) getProviders();
  }, [props.isModalVisible]);

  //use for the selected provider information that will be used for the redirectToCreate()
  const getProviders = () => {

    //this acts like selecting the provider dropdown list that will be used for the redirect
    //this one gets the primary care provider
    props.getProvider(props.authDetailState.primPcpKeyId, props.authDetailState.companyId)
      .then((response: any) => {
        setProvider(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });

    //this one to get the provider of the referralBy
    props.getProvider(props.authDetailState.authPcpKeyId, props.authDetailState.companyId)
      .then((response: any) => {
        setModifyAuthReferralBy(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const handleRemove = (fileId: string) => {
    setFileAttachments(fileAttachments.filter((item: any) => item.uid !== fileId));
  };

  // customRequest to just return a success to attach file to the form instead being actually uploading yet
  const customRequest = ({ onSuccess }: any) => {
    setTimeout(() => onSuccess('Success'), 0);
  };

  const beforeUpload = (file: any) => {

    const ext = file.name.substr(file.name.lastIndexOf('.') + 1);
    const allowFiles = ['tif', 'tiff', 'jpg', 'jpeg', 'pdf', 'doc', 'docx','TIF','TIFF','JPG','JPEG','PDF','DOC','DOCX'].includes(ext);
    if (!allowFiles) {
      message.error('You can only upload tif, tiff, jpg, jpeg, pdf, doc, docx, TIF, TIFF, JPG, JPEG, PDF, DOC, or DOCX file!');
      return false;
    } else {
      //file attachment used to list out the files under the dragger and cannot have duplicate files attached
      if (fileAttachments.filter(attachment => attachment.name === file.name).length === 0) {
        setFileAttachments((previous: any) => ([...previous, file]));
      } else {
        notifier.error('Please attach a file with a different name.');
      }
      return false;
    };
  }

  const handleOk = () => {

    form.validateFields().then(values => {
      props.isModalVisibleHandler();
      props.getPortalAuth(props.authDetailState.authNo, props.authDetailState.companyId, parseInt(sessionStorage.getItem('user_role')!))
        .then((response: any) => {
          //get updated auth status before submission, if status is now 1, give error and tell user to go back and use new auth mod process, otherwise continue on old one.
          if (parseInt(response.RmiStatus) === StatusCode.APPROVED) {
            notifier.error("Error: Status of this authorization was recently changed to approved, please return and search for the authorization then use the new approved authorization modification process.");
            handleCancel();
          }
          else {
            const createUploadFileData = new FormData();
            createUploadFileData.append("companyId", props.authDetailState.companyId);
            createUploadFileData.append("authNo", props.authDetailState.authNo);
            createUploadFileData.append("memberId", props.authDetailState.memberId);
            createUploadFileData.append("membLast", props.authDetailState.memberLastName);
            createUploadFileData.append("membFirst", props.authDetailState.memberFirstName);
            createUploadFileData.append("dob", moment(props.authDetailState.dateOfBirth).format('YYYY-MM-DD'));
            createUploadFileData.append("authPcpId", props.authDetailState.authPcpId);
            createUploadFileData.append("authPcpLast", props.authDetailState.authPcpLastName);
            createUploadFileData.append("authPcpFirst", props.authDetailState.authPcpFirstName);
            createUploadFileData.append("reqProvId", props.authDetailState.requestProv);
            createUploadFileData.append("reqProvLast", props.authDetailState.requestProviderLastName);
            createUploadFileData.append("reqProvFirst", props.authDetailState.requestProviderFirstName);
            createUploadFileData.append("date", moment().toString());
            fileAttachments.forEach(fileAttachment => {
              createUploadFileData.append("authFile[]", fileAttachment);
            });

            const submittedFileNames = fileAttachments.map((file: any) => file.name);

            const getSubmittedFileNamesData = {
              "AuthNo": props.authDetailState.authNo,
              "CompanyId": props.authDetailState.companyId,
              "SubmittedFileNames": submittedFileNames
            }

            props.uploadFilestoAuth(createUploadFileData)
              .then(() => {
                props.getSubmittedFileNames(getSubmittedFileNamesData)
                  .then((data: any[]) => {

                    const listOfSubmittedFileNames: string[] = data.map(x => x.StoredFileName);
                    const listOfOriginalFileNames: string[] = data.map(x => x.OriginalFileName);

                    // Extensions TODO:
                    const modifyAuthFormData = {
                      "RmiAction": values.action,
                      "RmiAttachments": listOfOriginalFileNames,
                      "RmiAuthNo": props.authDetailState.authNo,
                      "RmiCompanyId": props.authDetailState.companyId,
                      "RmiContact": values.contact,
                      "RmiEmail": values.email,
                      "RmiFax": values.faxNumber,
                      "RmiNote": values.reason,
                      "RmiPhone": values.phone,
                      "RmiStatus": props.authDetailState.statusNumber,
                      "RmiStatusDescr": props.authDetailState.status,
                      "RmiSubmittedFileNames": listOfSubmittedFileNames,
                      "RmiUser": props.user,
                    }

                    props.modifyAuth(modifyAuthFormData)
                    .then((response: number) => {
                      if (response === 1) {
                        notifier.success('Your request has been submitted.');
                      }
                    })
                    .catch((err: ErrorResponse) => {
                      notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
                      handleCancel();
                    });
                  });
              }).catch((err: ErrorResponse) => {
                notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
                handleCancel();
              });
          }
        });
      });
  };

  const handleCancel = () => {
    props.isModalVisibleHandler();
    setIsDisabledButton('');
  };

  const redirectToCreateAuth = () => {

    let form_data = {
      AuthMemberSearchModel: {
        "CompanyId":props.authDetailState.companyId,
        "MemberId": props.authDetailState.memberId ? props.authDetailState.memberId : null,
        "DateOfBirth": props.authDetailState.dateOfBirth !== '' ? props.authDetailState.dateOfBirth : null,
        "Provider": provider
      },
      UserIdModel: {
        "UserId": sessionStorage.user_id,
        "UserRole": sessionStorage.user_role,
      }
    };

    props.getAuthMember(form_data)
      .then((response: any[]) => {
        if (response.length === 0) {
          notifier.error('No existing authorization matches found.');
          return;
        } else {
          history.push({
            pathname: "/auth/modify-auth",
            state: {
              previousPath: "/auth/modify-auth",
              authDetailState: props.authDetailState,
              previousAuthNumber: props.authDetailState.authNo,
              modifyAuthReferralBy: modifyAuthReferralBy,
              provider: modifyAuthReferralBy,
              authMember: response,
              breadcrumbs: []
            }
          });
        }
      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const onSelect = (value: string) => {
    if (value === 'Yes') {
      props.isModalVisibleHandler();
      setIsDisabledButton('');
    } else {
      setIsDisabledButton('No');
    }
  }

  return (
    <>
      {props.authDetailState.status !== 'APPROVED' ? (
        <Modal
          visible={props.isModalVisible}
          width={750}
          onCancel={handleCancel}
          maskClosable={false}
          footer={[
            <div className="float-left">Required Fields <span className="asterisk">*</span></div>,
            <Button
              key="cancel"
              onClick={handleCancel}
            >
              Cancel
            </Button>,
            <Button
              type="primary"
              key="submit"
              form="modifyAuthForm"
              onClick={handleOk}
              loading={props.isModifyingAuth || props.isUploading}
            >
              Submit
            </Button>,
          ]}
        >

          <Title level={4} className="text-center">Request Authorization Modification</Title>

          <Form
            form={form}
            name="modifyAuthForm"
            onFinish={handleOk}
          >
            <FloatLabel
              label="Select Action"
              name="action"
              form={form}
              required
            >
              <Select>
                <Select.Option key={1} value="Authorization Modification">Authorization Modification</Select.Option>
              </Select>
            </FloatLabel>

            <div>
              <label htmlFor="authorization_no">
                Authorization No.:&nbsp;
              </label>
              <span className="authNo">
                {props.authDetailState.authNo}
              </span>
            </div>

            <div>
              <label htmlFor="ipa">
                IPA:&nbsp;
              </label>
              <span className="companyId">
                {getDisplayCompanyId(props.authDetailState.companyId)}
              </span>
            </div>

            <FloatLabel
              label="Contact"
              name="contact"
              form={form}
              required
            >
              <Input />
            </FloatLabel>

            <FloatLabel
              label="Email"
              name="email"
              form={form}
              required
              rules={[
                () => ({
                  async validator(_: any, value: string) {
                    if (!value || value.match(/\S+@\S+\.\S+/)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please enter a valid email address'));
                  },
                })
              ]}
            >
              <Input />
            </FloatLabel>

            <div className="row">
              <div className="col-lg-18">
                <FloatLabel
                  label="Phone"
                  name="phone"
                  form={form}
                  required
                  rules={[
                    () => ({
                      async validator(_: any, value: string) {
                        if (!value || value.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Please enter a valid phone number'));
                      },
                    })
                  ]}
                >
                  <Input />
                </FloatLabel>
              </div>

              <div className="col-lg-6">
                <FloatLabel
                  label="Ext"
                  name="phoneExt"
                  form={form}
                >
                  <Input />
                </FloatLabel>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-18">
                <FloatLabel
                  label="Fax Number"
                  name="faxNumber"
                  form={form}
                  required
                  rules={[
                    () => ({
                      async validator(_: any, value: string) {
                        if (!value || value.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Please enter a valid fax number'));
                      },
                    })
                  ]}
                >
                  <Input />
                </FloatLabel>
              </div>

              <div className="col-lg-6">
                <FloatLabel
                  label="Ext"
                  name="faxNumberExt"
                  form={form}
                >
                  <Input />
                </FloatLabel>
              </div>
            </div>

            <FloatLabel
              className="mb-3"
              label="Reason / Note"
              name="reason"
              form={form}
              required
            >
              <TextArea />
            </FloatLabel>

            <Dragger
              fileList={fileAttachments}
              customRequest={customRequest}
              beforeUpload={beforeUpload}
              multiple={true}
              showUploadList={false}
              accept={'.tif, .tiff, .jpg, .jpeg, .pdf, .doc, .docx, .TIF, .TIFF, .JPG, .JPEG, .PDF, .DOC, .DOCX'}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag a file at a time to this area to upload</p>
              <p className="ant-upload-hint">
                (Only files of type .tif, .tiff, .jpg, .jpeg, .pdf, .doc, .docx, .TIF, .TIFF, .JPG, .JPEG, .PDF, .DOC, .DOCX will be accepted)
              </p>
            </Dragger>

            {fileAttachments.map((file: any, index: number) => (
              <div key={index}>
                {file.name}{" "}
                <span><i onClick={() => handleRemove(file.uid)} className="fa fa-trash" aria-hidden="true"></i></span>
              </div>
            ))}

            <div className="d-flex justify-content-end">
              <Button
                type="default"
                onClick={() => setFileAttachments([])}
              >
                Clear All
              </Button>
            </div>
          </Form>
        </Modal>
      ) : (
        <>
          <Modal
            className="redirect-auth-modal"
            visible={props.isModalVisible}
            width={550}
            onCancel={handleCancel}
            maskClosable={false}
            footer={null}
          >
            <Title level={4} className="text-center">Request for Authorization Modification</Title>
              <label>Please validate if original authorization was used prior to this request</label>

              <Radio.Group value={isDisabledButton} onChange={(e) => onSelect(e.target.value)}>
                <Radio value={'Yes'}>Yes</Radio>
                <Radio value={'No'}>No</Radio>
              </Radio.Group>

            <div className="row">
              <div className="col-md-24">

                <Button
                  className="mt-4"
                  style={{left: "25%"}}
                  key="redirect"
                  type="primary"
                  onClick={() => redirectToCreateAuth()}
                  disabled={['Yes', ''].includes(isDisabledButton)}
                >
                  Submit New Authorization Request
                </Button>
              </div>
             </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default ModifyAuthDetailModal;